import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { collectionApi, saveApi, type SaveStateResponse, type UserCollection } from "lib/api";
import {
  graphCollectionsApi,
  mapGraphSnapshotToCanvasData,
} from "lib/api/graphCollections";

export const collectionQueryKeys = {
  userCollections: (entityType?: string | null, entityId?: number | null) =>
    ["collections", "user", entityType ?? "none", entityId ?? "none"] as const,
  saveState: (entityType: string, entityId: number) =>
    ["collections", "saveState", entityType, entityId] as const,
  collectionItems: (collectionId: number) => ["collections", "items", collectionId] as const,
  graphSnapshot: (collectionId: number) =>
    ["collections", "graphSnapshot", collectionId] as const,
};

const invalidateEntityCollectionState = async (
  queryClient: ReturnType<typeof useQueryClient>,
  entityType: string,
  entityId: number
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: collectionQueryKeys.userCollections(entityType, entityId),
    }),
    queryClient.invalidateQueries({
      queryKey: collectionQueryKeys.saveState(entityType, entityId),
    }),
  ]);
};

export function useUserCollections(
  options: {
    entityId?: number | null;
    entityType?: string | null;
    limit?: number;
    offset?: number;
  },
  enabled = true
) {
  const { entityId = null, entityType = null, limit, offset } = options;
  return useQuery({
    queryKey: collectionQueryKeys.userCollections(entityType, entityId),
    queryFn: async (): Promise<UserCollection[]> => {
      const response = await collectionApi.getUserCollections({
        entityId,
        entityType,
        limit,
        offset,
      });
      return response.collections;
    },
    enabled,
  });
}

export function useEntitySaveState(
  entityType: string,
  entityId: number,
  enabled = true
) {
  return useQuery({
    queryKey: collectionQueryKeys.saveState(entityType, entityId),
    queryFn: (): Promise<SaveStateResponse> => saveApi.getStatus(entityType, entityId),
    enabled,
  });
}

export function useCollectionMutation(entityType: string, entityId: number) {
  const queryClient = useQueryClient();

  const addMutation = useMutation({
    mutationFn: (collectionId: number) =>
      collectionApi.addItem(collectionId, { entityId, entityType }),
    onSuccess: async () => {
      await invalidateEntityCollectionState(queryClient, entityType, entityId);
    },
  });

  const removeMutation = useMutation({
    mutationFn: (collectionId: number) =>
      collectionApi.removeItem(collectionId, entityType, entityId),
    onSuccess: async () => {
      await invalidateEntityCollectionState(queryClient, entityType, entityId);
    },
  });

  return {
    addItem: addMutation.mutateAsync,
    removeItem: removeMutation.mutateAsync,
    isLoading: addMutation.isPending || removeMutation.isPending,
  };
}

export function useToggleSavedEntityMutation(entityType: string, entityId: number) {
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: () => saveApi.save(entityType, entityId),
    onSuccess: async () => {
      await invalidateEntityCollectionState(queryClient, entityType, entityId);
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: () => saveApi.unsave(entityType, entityId),
    onSuccess: async () => {
      await invalidateEntityCollectionState(queryClient, entityType, entityId);
    },
  });

  return {
    save: saveMutation.mutateAsync,
    unsave: unsaveMutation.mutateAsync,
    isLoading: saveMutation.isPending || unsaveMutation.isPending,
  };
}

/**
 * Fetches the graph snapshot for a graph collection and maps it to the
 * React Flow nodes/edges format expected by GraphCanvas and GraphCanvasSkeleton.
 * Used by CollectionCardRefactored (when no snapshot is pre-provided by the
 * parent) and CollectionProfile.
 *
 * The query is cached aggressively (30s staleTime) because snapshots change
 * infrequently and the same collection may appear in multiple lists.
 */
export function useGraphSnapshotQuery(collectionId?: number | null) {
  return useQuery({
    queryKey: collectionId
      ? collectionQueryKeys.graphSnapshot(collectionId)
      : ["collections", "graphSnapshot", "skip"],
    queryFn: () => graphCollectionsApi.getSnapshot(collectionId!),
    enabled: typeof collectionId === "number" && collectionId > 0,
    staleTime: 30_000,
    select: (data) => {
      const mapped = mapGraphSnapshotToCanvasData(data);
      if (mapped.nodes.length === 0) return null;
      return mapped;
    },
  });
}
